import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import WidgetCard from './WidgetCard';
import { useWidgetData } from './useWidgetData';

interface NetworkDay {
  date: string;
  revenue: number;
  /** Сколько аптек отчиталось за день */
  reported: number;
}

interface NetworkRegion {
  region: string;
  revenue: number;
  pharmacies: number;
}

interface NetworkRevenueResponse {
  pharmacies: number;
  days: NetworkDay[];
  weekTotal: number;
  lastFullDay: string | null;
  regions: NetworkRegion[];
}

const ACCENT = '#7c3aed';
const BAR_HEIGHT = 78;

const money = (v: number) => `${v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₸`;
const shortMoney = (v: number): string => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`;
  if (v >= 1_000) return `${Math.round(v / 1_000).toLocaleString('ru-RU')} тыс.`;
  return String(Math.round(v));
};
const dayLabel = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

/** Виджет «Выручка сети» (/admin): сумма Z-отчётов аптек в зоне видимости за 7 дней, топ регионов. Право: home.network_revenue.view */
const NetworkRevenueWidget: React.FC = () => {
  const { data, loading, error, reload } = useWidgetData<NetworkRevenueResponse>('/api/v2/home/revenue/network');
  const days = data?.days || [];
  const max = Math.max(0, ...days.map(d => d.revenue));
  const lastIdx = data?.lastFullDay ? days.findIndex(d => d.date === data.lastFullDay) : -1;
  const last = lastIdx >= 0 ? days[lastIdx] : null;
  const prev = lastIdx > 0 ? days[lastIdx - 1] : null;
  const delta = last && prev && prev.revenue > 0 ? ((last.revenue - prev.revenue) / prev.revenue) * 100 : null;
  const topRegion = data?.regions?.[0]?.revenue || 0;

  return (
    <WidgetCard
      title="Выручка сети"
      icon={<PaymentsOutlinedIcon />}
      accent={ACCENT}
      caption={data ? `${data.pharmacies} аптек` : undefined}
      loading={loading}
      error={error}
      onRefresh={reload}
      scroll={false}
    >
      {data && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1.5, md: 3 }, px: 2.5, py: 1.5 }}>
          <Box sx={{ flexShrink: 0, minWidth: { md: 170 }, pr: { md: 3 }, borderRight: { md: '1px solid #f1f5f9' } }}>
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.2 }}>
              {last ? `Последний день, ${dayLabel(last.date)}` : 'Последний день'}
            </Typography>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>
              {last ? money(last.revenue) : '-'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, minHeight: 16, color: delta == null ? '#94a3b8' : delta >= 0 ? '#15803d' : '#b91c1c' }}>
              {delta == null ? 'нет данных для сравнения' : `${delta >= 0 ? '▲' : '▼'} ${Math.abs(delta).toFixed(1).replace('.', ',')}% к предыдущему дню`}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', mt: 1, lineHeight: 1.2 }}>Итого за 7 дней</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{money(data.weekTotal)}</Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ height: BAR_HEIGHT + 16, display: 'flex', alignItems: 'flex-end', gap: 1, borderBottom: '1px solid #e2e8f0' }}>
              {days.map(d => {
                const pct = max > 0 ? Math.max(3, (d.revenue / max) * 100) : 0;
                return (
                  <Tooltip key={d.date} placement="top" title={`${dayLabel(d.date)}: ${money(d.revenue)} · отчитались ${d.reported} из ${data.pharmacies}`}>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                      {d.revenue > 0 && (
                        <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#475569', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{shortMoney(d.revenue)}</Typography>
                      )}
                      <Box
                        sx={{
                          width: '100%', maxWidth: 40, height: `${(pct / 100) * BAR_HEIGHT}px`, minHeight: 3, borderRadius: '5px 5px 0 0',
                          background: d.date === data.lastFullDay ? `linear-gradient(180deg, ${ACCENT}, #5b21b6)` : '#c4b5fd'
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
              {days.map(d => (
                <Typography key={d.date} sx={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 10.5, color: '#64748b' }}>{dayLabel(d.date)}</Typography>
              ))}
            </Box>
          </Box>

          <Box sx={{ flexShrink: 0, width: { md: 220 }, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.2, mb: 0.5 }}>
              Регионы{last ? `, ${dayLabel(last.date)}` : ''}
            </Typography>
            {data.regions.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>Нет данных</Typography>
            ) : (
              data.regions.slice(0, 4).map(r => (
                <Box key={r.region} sx={{ mb: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: 12.5, color: '#334155' }} noWrap>{r.region}</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{shortMoney(r.revenue)}</Typography>
                  </Box>
                  <Box sx={{ height: 3, borderRadius: 2, bgcolor: '#f1f5f9' }}>
                    <Box sx={{ height: '100%', borderRadius: 2, bgcolor: '#a78bfa', width: `${topRegion > 0 ? (r.revenue / topRegion) * 100 : 0}%` }} />
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}
    </WidgetCard>
  );
};

export default NetworkRevenueWidget;
